import React, {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { sendClientSideSplitTrackEvent } from "@leafly-com/split-next";
import { useDispatch, useSelector } from "react-redux";

import getVariant from "api/requests/getVariant";
import postReservation from "api/requests/postReservation";
import { retrieveCart } from "api/requests/retrieveCart";
import { Cart } from "custom-types/Cart";
import { Coordinates } from "custom-types/Coordinates";
import { MenuItem, MenuItemVariant } from "custom-types/MenuItem";
import { useEventTracker } from "hooks/useEventTracker";
import { setUserMenuPreference } from "redux/action-creators/user";
import {
  AddedToCartAction,
  LoadCartAction,
  RESERVATION_ADDED_TO_BAG,
  RESERVATION_LOAD_CART,
} from "redux/reducers/reservation";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCartId } from "redux/selectors/reservation";
import {
  MENU_TITLES,
  MenuType,
  testVariantsForMenuType,
} from "utils/menuTypeUtils";
import { getSplitKey } from "utils/split/getSplitKey";
import { Action, Category } from "utils/trackEvent";

import { Modal } from "components/botanic/Modal";

import { AddToBagConfirmation } from "./internal/AddToBagConfirmation";
import { MenuItemUnavailableNotice } from "./internal/MenuItemUnavailableNotice";
import { NewBag } from "./internal/NewBag";
import { VariantSelection } from "./internal/VariantSelection";

enum ModalState {
  BagFailed,
  Confirmation,
  VariantSelection,
  ResetBag,
  ResetMenu,
  Inactive,
}

type AddToBagModalProps = ComponentPropsWithoutRef<typeof Modal> & {
  /**
   * quantity passed to reserve button
   */
  quantity?: number;
  /**
   * A split event to fire when something is added to the user's bag.
   */
  splitEventName?: string;
  /**
   * The name of the page that the split event is fired on.
   */
  splitPageProperty?: string;
  /**
   * A set of latitude and longitude coordinates for the user's current
   * location, used to display the distance to the menu item's dispensary.
   */
  userLocation?: Coordinates;
  /**
   * The ID of the menu item variant that should be displayed in the modal.
   * The associated menu item and all variants will be fetched, with the
   * variant indicated by this ID being the first option selected.
   */
  variantId?: number;
  /**
   * Tells the component to just add the selected variant to bag even if
   * there are multiple variants
   */
  immediatelyAddToBag?: boolean;
  /**
   * A callback that fires when modal has transitioned completely
   * to 'inactive' state and when add to bag is complete
   */
  onDismiss?: () => void;
  /**
   * A callback that fires when modal has successful reservation
   */
  onReservation?: () => void;
  /**
   * A callback that fires when modal has reservation failure
   */
  onReservationFail?: () => void;
  /**
   * Tell the add to bag action to use ceph add to bag popover instead of
   * confirmation screen within modal
   */
  useAddedToBagPopout?: boolean;
  /**
   * Flag to represent whether associated dispensary is part of the Shop
   * Every Menu MVP test group
   */
  shopEveryMenuMVPEnabled?: boolean;
};

export const noop = (): void => {
  /* do nothing */
};

/**
 * AddToBagModal displays the complete workflow for adding a menu item to the
 * bag. Most of the components in this collection of shared components are
 * presentational only, and this component breaks this pattern.
 */
export const AddToBagModal: React.FC<AddToBagModalProps> = ({
  immediatelyAddToBag,
  onDismiss = noop,
  onReservation = noop,
  onReservationFail = noop,
  splitEventName,
  splitPageProperty,
  useAddedToBagPopout,
  userLocation,
  quantity = 1,
  variantId,
  shopEveryMenuMVPEnabled,
  ...props
}) => {
  const domainCountryCode = useSelector(getDomainCountryCode);
  const cartId = useSelector(getCartId);

  const { publishEvent } = useEventTracker();
  const dispatch = useDispatch();

  const splitKey = getSplitKey();

  const [menuItem, setMenuItem] = useState<MenuItem>();
  const [menuType, setMenuType] = useState<MenuType>();
  const [addedVariant, setAddedVariant] = useState<MenuItemVariant>();
  const [selectedVariantId, setSelectedVariantId] = useState<number>();
  const [staleBag, setStaleBag] = useState<Cart>();
  const [requiresNewBag, setRequiresNewBag] = useState<boolean>(false);
  const [modalState, setModalState] = useState<ModalState>(ModalState.Inactive);

  const fetchCart = async () => {
    if (cartId) {
      const { cart } = await retrieveCart({
        cartId: cartId,
        domainCountryCode: domainCountryCode,
      });

      if (cart) {
        dispatch<LoadCartAction>({
          cart,
          type: RESERVATION_LOAD_CART,
        });

        return cart;
      }
    }
    return;
  };

  const fetchStaleCart = async () => {
    const cart = await fetchCart();

    if (cart) {
      setStaleBag(cart);
    }
  };

  const onReset = useCallback(() => {
    onDismiss();
    setRequiresNewBag(false);
    setStaleBag(undefined);
  }, [onDismiss]);

  const addToBag = async (variant: MenuItemVariant) => {
    if (!variant || !cartId || !menuItem) {
      return;
    }

    await postReservation({
      cartId,
      forceUpdate: requiresNewBag,
      menuItemId: menuItem.menuItemId,
      quantity,
      variant,
    })
      .then(async () => {
        await sendClientSideSplitTrackEvent(
          "leafly_global_addToBagClick",
          splitKey
        );
        splitEventName &&
          (await sendClientSideSplitTrackEvent(
            {
              eventType: splitEventName,
              properties: { page: splitPageProperty || null },
            },
            splitKey
          ));
        setAddedVariant(variant);

        onReservation();

        dispatch<AddedToCartAction>({
          type: RESERVATION_ADDED_TO_BAG,
          variantId: variant.id,
        });

        await fetchCart();

        if (useAddedToBagPopout) {
          if (modalState === ModalState.Inactive) {
            onReset();
          } else {
            setModalState(ModalState.Inactive);
          }
        } else {
          setModalState(ModalState.Confirmation);
        }
      })
      .catch(async (error: Error) => {
        if (error.message.includes("cart_mismatch_error")) {
          await fetchStaleCart();

          setRequiresNewBag(true);
          setModalState(ModalState.ResetBag);
        } else if (error.message.includes("multiple_menu_type_error")) {
          setRequiresNewBag(true);
          setModalState(ModalState.ResetMenu);
        } else {
          await fetchStaleCart();

          setModalState(ModalState.BagFailed);

          publishEvent({
            action: Action.impression,
            category: Category.bag,
            errorMessage: error.message,
            label: shopEveryMenuMVPEnabled
              ? "add to bag error - shop every menu"
              : "add to bag error",
          });
        }

        onReservationFail();
      });
  };

  const onMenuTypeChange = (menuType: MenuType) => {
    const variants = menuItem?.variants || [];
    /**
     * At least one variant of this menu type must be present in order for this
     * callback to be invoked by the MenuTypeButton.
     */
    const variant = variants.find(testVariantsForMenuType(menuType));

    setSelectedVariantId(variant?.id);
    setMenuType(menuType);
    dispatch(setUserMenuPreference(menuType));
  };

  const onAddToBagClick = async () => {
    const variant = menuItem?.variants.find((v) => v.id === selectedVariantId);
    if (variant) {
      addToBag(variant);
    }
  };

  const title = () => {
    switch (modalState) {
      case ModalState.Confirmation:
        return "Added to cart";
      case ModalState.VariantSelection:
        return "Choose your amount";
      case ModalState.BagFailed:
        return "Uh oh!";
      case ModalState.ResetBag:
        return shopEveryMenuMVPEnabled
          ? "Create a new in-store cart?"
          : "Create a new cart?";
      case ModalState.ResetMenu: {
        const pendingBagItemMenuTypeLabels =
          menuType === MenuType.Med
            ? MENU_TITLES.med.shortLower
            : MENU_TITLES.rec.shortLower;
        return `Switch to a ${pendingBagItemMenuTypeLabels} cart?`;
      }
      default:
        return "Add to cart";
    }
  };

  /**
   * This useEffect will fetch the menu item and variants data based on the
   * variantId prop, and set an initial menu type for dual-licensed dispensaries.
   */
  useEffect(() => {
    if (variantId && domainCountryCode) {
      getVariant(variantId, domainCountryCode).then((response) => {
        if (response) {
          const initialVariant = response.variants?.find(
            (variant) => variant.id === variantId
          );

          setSelectedVariantId(variantId);
          setMenuItem(response);
          setMenuType(initialVariant?.medical ? MenuType.Med : MenuType.Rec);
        } else {
          setModalState(ModalState.BagFailed);
          onReservationFail();
        }
      });
    }
  }, [variantId, domainCountryCode]);

  // Once we get the initial menu items from the variantId prop, we'll avoid
  // showing variant selection if there is only one variant or immediatelyAddToBag prop is
  // set to true and go straight to add to bag. Otherwise, we'll show the variant
  // selection.
  useEffect(() => {
    if (menuItem) {
      if (menuItem.variants?.length === 1 || immediatelyAddToBag) {
        const variant = menuItem.variants.find((v) => v.id === variantId);
        if (variant) {
          addToBag(variant);
        }
      } else {
        setModalState(ModalState.VariantSelection);
      }
    }
  }, [menuItem]);

  // Adding this ref ensures that we don't do anything in this useEffect
  // right off the bat, as useEffect runs once on render and also when
  // modalState is changed
  const prevModalStateRef = useRef<ModalState>(modalState);
  useEffect(() => {
    if (
      modalState !== prevModalStateRef.current &&
      modalState === ModalState.Inactive
    ) {
      // Probably over precautious, but lets make sure that the modal has been rendered
      // inactive by state change before setting state and app-side dismiss.
      onReset();
    }

    prevModalStateRef.current = modalState;
  }, [modalState, onReset]);

  return modalState !== ModalState.Inactive ? (
    <Modal
      title={title()}
      onDismissed={() => setModalState(ModalState.Inactive)}
      width={500}
      {...props}
    >
      {({ dismiss }: { dismiss: () => void }) => (
        <>
          {modalState === ModalState.BagFailed && (
            <MenuItemUnavailableNotice
              menuItem={menuItem}
              onKeepShoppingClick={dismiss}
              staleBag={staleBag}
            />
          )}

          {modalState === ModalState.Confirmation && (
            <AddToBagConfirmation
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- menuItem is guaranteed to exist for ModalState.Confirmation
              menuItem={menuItem!}
              onKeepShoppingClick={dismiss}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- addedVariant is guaranteed to exist for ModalState.Confirmation
              addedVariant={addedVariant!}
              quantity={quantity}
              userLocation={userLocation}
            />
          )}

          {modalState === ModalState.VariantSelection && (
            <VariantSelection
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- menuItem is guaranteed to exist for ModalState.VariantSelection
              menuItem={menuItem!}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- menuType is guaranteed to exist for ModalState.VariantSelection
              menuType={menuType!}
              onAddToBagClick={onAddToBagClick}
              onMenuTypeChange={onMenuTypeChange}
              onVariantSelect={(id) => {
                setSelectedVariantId(id);
              }}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- selectedVariantId is guaranteed to exist for ModalState.VariantSelection
              selectedVariantId={selectedVariantId!}
              shopEveryMenuMVPEnabled={shopEveryMenuMVPEnabled}
            />
          )}

          {(modalState === ModalState.ResetBag ||
            modalState === ModalState.ResetMenu) && (
            <NewBag
              newDispensary={menuItem?.dispensary}
              staleDispensary={staleBag?.dispensaryData}
              currentMenuType={menuType}
              messaging={
                modalState === ModalState.ResetBag ? "dispensary" : "menu"
              }
              onKeepBagClick={dismiss}
              onNewBagClick={onAddToBagClick}
              shopEveryMenuMVPEnabled={shopEveryMenuMVPEnabled}
            />
          )}
        </>
      )}
    </Modal>
  ) : null;
};
